/**
 * Account Success Message Handler
 * Handles displaying success messages based on URL hash for account creation
 */
class AccountSuccessMessage {
  constructor() {
    this.successMessage = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.handleSuccess());
    } else {
      this.handleSuccess();
    }

    // Listen for hash changes
    window.addEventListener('hashchange', () => this.handleSuccess());
  }

  handleSuccess() {
    const hash = window.location.hash;

    // Check if we have a success hash
    if (hash === '#account-created' || hash === '#success') {
      this.showSuccessMessage();
      // Remove hash from URL after showing message to prevent re-triggering
      this.removeHashFromURL();
    }
  }

  showSuccessMessage() {
    // Remove any existing success message
    this.removeSuccessMessage();

    // Create the success message element
    this.createSuccessMessage();

    // Insert it at the top of the account page
    this.insertSuccessMessage();

    // Focus on the message for accessibility
    this.focusSuccessMessage();

    // Auto-hide after 10 seconds (optional)
    this.scheduleAutoHide();
  }

  createSuccessMessage() {
    this.successMessage = document.createElement('div');
    this.successMessage.className = 'account-success-message';
    this.successMessage.setAttribute('role', 'status');
    this.successMessage.setAttribute('aria-live', 'polite');
    this.successMessage.setAttribute('aria-atomic', 'true');
    this.successMessage.setAttribute('tabindex', '-1');

    // Get localized text (fallback to English if not available)
    const successTitle = window.theme?.strings?.account_success_title || 'Welcome! Your account has been created successfully.';
    const successSubtitle = window.theme?.strings?.account_success_subtitle || 'You can now place orders and track your purchase history.';
    const closeLabel = window.theme?.strings?.close || 'Close';

    this.successMessage.innerHTML = `
      <svg class="account-success-message__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22,4 12,14.01 9,11.01"></polyline>
      </svg>
      <div class="account-success-message__content">
        <h2 class="account-success-message__title h5">${successTitle}</h2>
        <p class="account-success-message__subtitle">${successSubtitle}</p>
      </div>
      <button class="account-success-message__close" type="button" aria-label="${closeLabel}">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    `;

    // Add close functionality
    const closeButton = this.successMessage.querySelector('.account-success-message__close');
    closeButton.addEventListener('click', () => this.hideSuccessMessage());

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.successMessage && !this.successMessage.getAttribute('aria-hidden')) {
        this.hideSuccessMessage();
      }
    });
  }

  insertSuccessMessage() {
    // Since we're using fixed positioning, append directly to body
    // This ensures the modal appears above all other content
    document.body.appendChild(this.successMessage);
  }

  focusSuccessMessage() {
    if (this.successMessage) {
      // Small delay to ensure the element is in the DOM and styled
      setTimeout(() => {
        this.successMessage.focus();
        // No need to scroll since it's a fixed positioned modal at the top
      }, 100);
    }
  }

  hideSuccessMessage() {
    if (this.successMessage) {
      this.successMessage.setAttribute('aria-hidden', 'true');
      // Add fade out animation with proper transform for modal
      this.successMessage.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      this.successMessage.style.opacity = '0';
      this.successMessage.style.transform = 'translateX(-50%) translateY(-100%)';

      setTimeout(() => {
        this.removeSuccessMessage();
      }, 300);
    }
  }

  removeSuccessMessage() {
    if (this.successMessage && this.successMessage.parentNode) {
      this.successMessage.parentNode.removeChild(this.successMessage);
      this.successMessage = null;
    }
  }

  scheduleAutoHide() {
    // Auto-hide after 10 seconds for better UX
    setTimeout(() => {
      if (this.successMessage && !this.successMessage.getAttribute('aria-hidden')) {
        this.hideSuccessMessage();
      }
    }, 10000);
  }

  removeHashFromURL() {
    // Remove hash without triggering hashchange event
    if (window.history && window.history.replaceState) {
      const url = window.location.href.split('#')[0];
      window.history.replaceState(null, '', url);
    }
  }
}

// Initialize when the script loads
if (typeof window !== 'undefined') {
  new AccountSuccessMessage();
}
